import { useStowageStore } from 'src/context/StowageContext';
import useApiEndpoints from 'src/context/useApiEndpoints';
import useApi from '../useApi';
import { useEffect } from 'react';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { createEmpyStowagePlan, mapStowagePlans } from 'src/factory/Stowage/StowagePlanFactory';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import { useAuthStore } from 'src/context/AuthContext';
import { UUID } from 'crypto';
import { StowagePlanChangeBM } from 'src/types/BusinessModel/Stowage/StowagePlanChangeBM';
import { mapStowagePlanChanges } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import { sortStowagePlans, getMasterStowagePlan, createStowagePlanName } from 'src/utils/stowage/stowagePlanUtils';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { useVesselStore } from 'src/context/VesselContext';
import { getAllStowagePlanItems } from './offlineStowageApi';
import { filterAndMapOfflineStowagePlans } from 'src/factory/Stowage/OfflineStowagePlanFactory';
import { filterAndMapOfflineStowagePlanChanges } from 'src/factory/Stowage/OfflineStowagePlanChangeFactory';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { state, getApiData } = useApi();
    const { loggedInUser } = useAuthStore();
    const { setStowagePlans, setStowagePlanChanges } = useStowageStore();
    const { processApiEndpointError } = apiEndpointUtils();
    const { getQueryParamValueFromUrl } = useQueryParamsHandler();
    const { setQueryParamStowagePlan } = useStowageQueryParamsUtils();
    const { selectedVessel } = useVesselStore();

    // TODO: VALIDATION - Add from date or date range parameters to filter stowage plans by date criteria for better API performance and data relevance
    const getStowagePlans = (imoNumber: number) => {
        return getApiData({
            endpoint: apiEndpoints['getStowagePlans'].url,
            queryParam: buildQueryParams({ imoNumber }),
            additionalInfo: { imoNumber },
        });
    };

    // TODO: VALIDATION - Add from date or date range parameters to filter stowage plan changes by date criteria for better API performance and data relevance
    const getStowagePlanChanges = async (stowagePlanGuid: UUID, forceRefresh: boolean = false) => {
        const result = await getApiData({
            endpoint: apiEndpoints['getStowagePlanChanges'].url,
            queryParam: buildQueryParams({
                stowagePlanGuid: stowagePlanGuid.toString(),
            }),
            forceRefreshCache: forceRefresh,
        });

        const _offlineStowagePlanItems = await getAllStowagePlanItems();
        const _offlineStowagePlanChanges = filterAndMapOfflineStowagePlanChanges(_offlineStowagePlanItems, selectedVessel.imoNumber, stowagePlanGuid);

        const bms = result ? (result as StowagePlanChangeBM[]) : [];
        const _stowagePlanChanges = mapStowagePlanChanges(bms);

        // Merge offline stowage plan changes with online ones
        _offlineStowagePlanChanges.forEach((offlineChange) => {
            const existingChangeIndex = _stowagePlanChanges.findIndex(
                (onlineChange) => onlineChange.stowagePlanChangeGuid === offlineChange.stowagePlanChangeGuid
            );

            if (existingChangeIndex >= 0) {
                _stowagePlanChanges[existingChangeIndex] = offlineChange;
            } else {
                _stowagePlanChanges.push(offlineChange);
            }
        });

        setStowagePlanChanges([..._stowagePlanChanges]);

        return _stowagePlanChanges;
    };

    useEffect(() => {
        (async () => {
            if (state.status === 'success') {
                if (state.endpoint === apiEndpoints['getStowagePlans'].url) {
                    const bms = state.result as StowagePlanBM[];
                    const _offlineStowagePlanItems = await getAllStowagePlanItems();
                    const _offlineStowagePlans = filterAndMapOfflineStowagePlans(_offlineStowagePlanItems, selectedVessel.imoNumber);
                    let _stowagePlans = mapStowagePlans(bms);

                    _offlineStowagePlans.forEach((offlinePlan) => {
                        const existingPlanIndex = _stowagePlans.findIndex((onlinePlan) => onlinePlan.stowagePlanGuid === offlinePlan.stowagePlanGuid);

                        if (existingPlanIndex >= 0) {
                            _stowagePlans[existingPlanIndex] = offlinePlan;
                        } else {
                            _stowagePlans.push(offlinePlan);
                        }
                    });

                    let _stowagePlanGuid;

                    if (_stowagePlans.filter((sp) => !sp.isDeleted).length === 0) {
                        const _stowagePlan = createEmpyStowagePlan(state.additionalInfo.imoNumber, loggedInUser.fullName, null, '', 0);
                        _stowagePlan.title = createStowagePlanName(_stowagePlan, selectedVessel);
                        _stowagePlans.push(_stowagePlan);
                        _stowagePlanGuid = _stowagePlan.stowagePlanGuid;
                    }
                    _stowagePlans = sortStowagePlans(_stowagePlans);

                    if (!_stowagePlanGuid) {
                        _stowagePlanGuid = getQueryParamValueFromUrl('stowagePlanGuid');

                        if (!_stowagePlanGuid) {
                            const masterStowagePlan = getMasterStowagePlan(_stowagePlans);
                            _stowagePlanGuid = masterStowagePlan ? masterStowagePlan.stowagePlanGuid : _stowagePlans[0].stowagePlanGuid;
                        }
                    }
                    setStowagePlans(_stowagePlans);
                    setQueryParamStowagePlan(_stowagePlanGuid, ['portCallGuid', 'berthCallGuid', 'cargoOperationGuid']);
                }
            } else {
                processApiEndpointError(state);
            }
        })();
    }, [state]);

    return { getStowagePlans, getStowagePlanChanges };
};
